import React, { useEffect, useRef, useState } from 'react';
import { Chip, BaseButton, Modal, Toast } from '@repo/common';
import FilterModal from './FilterModal';
import { ReactComponent as FilterIcon } from '@repo/common/assets/icons/16px/icon_16px_filter.svg';
import { ReactComponent as DeleteIcon } from '@repo/common/assets/icons/16px/icon_16px_delete_outline.svg';
import { ReactComponent as PlusIcon } from '@repo/common/assets/icons/16px/icon_16px_plus_outline.svg';
import {
  permanentlyDeleteWorkshopSlots,
  softDeleteWorkshopSlots,
} from '../../../api/workshop/workshop.api';

const WorkshopFilterHeader = ({
  currentFilter,
  onFilterChange,
  selectedIds,
  onDeleteSuccess,
  singleDeleteSlotId,
  onSingleDeleteDone,
  singleSoftDeleteSlotId,
  onSingleSoftDeleteDone,
  appliedFilters,
  onFiltersApply,
}) => {
  const filters = ['By Date', 'By Workshop Type', 'Deleted Workshops'];
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterCoords, setFilterCoords] = useState({ top: 0, left: 0 });
  const [deletedCount, setDeletedCount] = useState(0);
  const filterButtonRef = useRef(null);
  const [showPermanentlyDeleteModal, setshowPermanentlyDeleteModal] =
    useState(false);
  const [
    showPermanentlyDeleteSuccessToast,
    setShowPermanentlyDeleteSuccessToast,
  ] = useState(false);
  const [showDeleteModal, setshowDeleteModal] = useState(false);
  const [showDeleteSuccessToast, setShowDeleteSuccessToast] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (singleDeleteSlotId) {
      setshowPermanentlyDeleteModal(true);
    }
  }, [singleDeleteSlotId]);

  useEffect(() => {
    if (singleSoftDeleteSlotId) {
      setshowDeleteModal(true);
    }
  }, [singleSoftDeleteSlotId]);

  const isSingleDelete = !!singleDeleteSlotId;
  const isSingleSoftDelete = !!singleSoftDeleteSlotId;
  const count = selectedIds.size;
  const isPlural = count > 1;

  const handleFilterClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isFilterOpen) {
      setIsFilterOpen(false);
      return;
    }

    if (filterButtonRef.current) {
      const rect = filterButtonRef.current.getBoundingClientRect();
      const parentRect = e.currentTarget
        .closest('.filter-header-container')
        .getBoundingClientRect();

      const PANEL_WIDTH = 248;
      setFilterCoords({
        top: rect.bottom - parentRect.top - 36,
        left: Math.max(
          0,
          Math.min(rect.right - parentRect.left + 10, parentRect.width - PANEL_WIDTH),
        ),
      });
    }
    setIsFilterOpen(true);
  };

  const handleCancelDelete = () => {
    setshowPermanentlyDeleteModal(false);
    setshowDeleteModal(false);
    if (isSingleDelete) onSingleDeleteDone?.(false);
    if (isSingleSoftDelete) onSingleSoftDeleteDone?.(false);
  };

  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true);

      if (isSingleDelete) {
        setDeletedCount(1);
        await permanentlyDeleteWorkshopSlots([singleDeleteSlotId]);
        setshowPermanentlyDeleteModal(false);
        setShowPermanentlyDeleteSuccessToast(false);
        setTimeout(() => setShowPermanentlyDeleteSuccessToast(true), 10);
        onSingleDeleteDone?.(true);
      } else if (isSingleSoftDelete) {
        setDeletedCount(1);
        await softDeleteWorkshopSlots([singleSoftDeleteSlotId]);
        setshowDeleteModal(false);
        setShowDeleteSuccessToast(false);
        setTimeout(() => setShowDeleteSuccessToast(true), 10);
        onSingleSoftDeleteDone?.(true);
      } else if (currentFilter === 'Deleted Workshops') {
        setDeletedCount(count);
        await permanentlyDeleteWorkshopSlots([...selectedIds]);
        setshowPermanentlyDeleteModal(false);
        setShowPermanentlyDeleteSuccessToast(false);
        setTimeout(() => setShowPermanentlyDeleteSuccessToast(true), 10);
        onDeleteSuccess();
      } else {
        setDeletedCount(count);
        await softDeleteWorkshopSlots([...selectedIds]);
        setshowDeleteModal(false);
        setShowDeleteSuccessToast(false);
        setTimeout(() => setShowDeleteSuccessToast(true), 10);
        onDeleteSuccess();
      }
    } catch (error) {
      console.error('Delete failed:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className='filter-header-container relative flex flex-col tabletH:flex-row gap-3 px-1 tabletH:px-3 pb-4 tabletH:justify-between tabletH:items-end w-full min-w-0'>
      <div className='flex flex-wrap items-center gap-2'>
        {filters.map((filter) => (
          <Chip
            key={filter}
            type='fill'
            state={
              filter === 'By Workshop Type'
                ? 'disabled'
                : currentFilter === filter
                  ? 'selected'
                  : 'default'
            }
            onClick={
              filter === 'By Workshop Type'
                ? undefined
                : () => onFilterChange(filter)
            }
            hideIcon
          >
            {filter}
          </Chip>
        ))}
      </div>

      <div className='flex flex-wrap items-end gap-2'>
        <button
          ref={filterButtonRef}
          onClick={handleFilterClick}
          className={`w-[30px] h-[30px] flex justify-center items-center rounded-lg
            ${
              isFilterOpen
                ? 'text-icon-secondary bg-grayscale-gray02'
                : 'text-icon-tertiary hover:text-icon-secondary'
            }`}
        >
          <FilterIcon />
        </button>
        <BaseButton
          type='text_basic'
          size='s'
          icon={<DeleteIcon />}
          gap='gap-1'
          bgclass='py-1.5 pl-2 pr-3'
          onClick={() =>
            currentFilter === 'Deleted Workshops'
              ? setshowPermanentlyDeleteModal(true)
              : setshowDeleteModal(true)
          }
          isDisabled={count === 0}
        >
          {currentFilter === 'Deleted Workshops'
            ? 'Delete permanently'
            : 'Delete'}
        </BaseButton>
        <BaseButton
          size='s'
          icon={<PlusIcon />}
          gap='gap-0.5'
          bgclass='py-1.5 pl-2 pr-3'
          isDisabled
        >
          Create Workshop
        </BaseButton>
      </div>

      {isFilterOpen && (
        <FilterModal
          coords={filterCoords}
          onClose={() => setIsFilterOpen(false)}
          onApply={async (newFilters) => {
            await onFiltersApply?.(newFilters);
          }}
          initialValues={appliedFilters}
        />
      )}

      {(showDeleteModal || showPermanentlyDeleteModal) && (
        <Modal
          isOpen
          onClickLeftBtn={handleCancelDelete}
          onClickRightBtn={handleConfirmDelete}
          title={
            showDeleteModal
              ? `Delete ${isPlural ? `${count} workshops` : 'workshop'}?`
              : 'Delete permanently?'
          }
          subText={
            showDeleteModal
              ? `This will cancel any participant bookings and delete the workshop${
                  isPlural ? 's' : ''
                }. This action cannot be undone.`
              : `This will permanently delete the selected workshop(s) and all related records. This action cannot be undone.`
          }
          buttonSize='xl'
          leftButtonLabel={showDeleteModal ? 'Keep Workshop' : 'Cancel'}
          leftHideIcon={true}
          rightButtonLabel={
            showDeleteModal ? 'Delete & Cancel All' : 'Delete permanently'
          }
          rightHideIcon={true}
          type='criticalError'
          rightButtonLoading={isDeleting}
        />
      )}

      {showDeleteSuccessToast && (
        <div className='fixed bottom-6 left-0 right-0 px-4 z-[1000] flex justify-center'>
          <div
            className='w-full'
            style={{ maxWidth: deletedCount > 1 ? '250px' : '228px' }}
          >
            <Toast
              title={`${
                deletedCount > 1 ? `${deletedCount} Workshops` : 'Workshop'
              } deleted.`}
              hierarchy='important'
              direction='up'
              customWidth='100%'
            />
          </div>
        </div>
      )}

      {showPermanentlyDeleteSuccessToast && (
        <div className='fixed bottom-6 left-0 right-0 px-4 z-[1000] flex justify-center'>
          <div className='w-full' style={{ maxWidth: '346px' }}>
            <Toast
              title={'Workshop(s) permanently deleted.'}
              hierarchy='important'
              direction='up'
              customWidth='100%'
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkshopFilterHeader;
